import { motion } from "framer-motion";
import { Sparkles, TrendingUp, Award, Star } from "lucide-react";
import { cn } from "@/lib/utils";

export type CreatorTier = "top1" | "top5" | "top15" | "emerging";

interface CreatorTierBadgeProps {
  tier: CreatorTier;
  className?: string;
  showIcon?: boolean;
}

export const getCreatorTierFromScore = (avgGeoScore: number, promptCount: number): CreatorTier => {
  if (promptCount < 3) return "emerging";
  if (avgGeoScore >= 90) return "top1";
  if (avgGeoScore >= 80) return "top5";
  if (avgGeoScore >= 65) return "top15";
  return "emerging";
};

export const getCreatorTierLabel = (tier: CreatorTier): string => {
  switch (tier) {
    case "top1": return "Top 1% Creator";
    case "top5": return "Top 5% Creator";
    case "top15": return "Top 15% Creator";
    default: return "Emerging Creator";
  }
};

export const getCreatorTierColors = (tier: CreatorTier) => {
  switch (tier) {
    case "top1":
      return {
        bg: "bg-gradient-to-r from-amber-500/20 to-yellow-400/20",
        border: "border-amber-400/50",
        text: "text-amber-300",
        glow: "shadow-amber-500/20"
      };
    case "top5":
      return {
        bg: "bg-gradient-to-r from-purple-500/20 to-fuchsia-400/20",
        border: "border-purple-400/50",
        text: "text-purple-300",
        glow: "shadow-purple-500/20"
      };
    case "top15":
      return {
        bg: "bg-gradient-to-r from-cyan-500/20 to-blue-400/20",
        border: "border-cyan-400/50",
        text: "text-cyan-300",
        glow: "shadow-cyan-500/20"
      };
    default:
      return {
        bg: "bg-muted/30",
        border: "border-muted-foreground/30",
        text: "text-muted-foreground",
        glow: ""
      };
  }
};

const getTierIcon = (tier: CreatorTier) => {
  switch (tier) {
    case "top1": return Award;
    case "top5": return Sparkles;
    case "top15": return TrendingUp;
    default: return Star;
  }
};

export function CreatorTierBadge({ tier, className, showIcon = true }: CreatorTierBadgeProps) {
  const colors = getCreatorTierColors(tier);
  const Icon = getTierIcon(tier);
  const label = getCreatorTierLabel(tier);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className={cn(
        "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border",
        colors.bg,
        colors.border,
        colors.text,
        colors.glow && `shadow-lg ${colors.glow}`,
        className
      )}
    >
      {showIcon && <Icon className="w-3.5 h-3.5" />}
      <span>{label}</span>
    </motion.div>
  );
}
